"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Loader2,
  ArrowRight,
  CheckCircle2,
  Mail,
  Building2,
} from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  signIn,
  selectOrg,
  sendMagicLink,
  requestPasswordReset,
} from "@/actions/auth";
import type { OrgOption } from "@/actions/auth";
import { useTranslations } from "next-intl";
import { useNavigationProgress } from "@/components/layout/navigation-progress";
import { useInviteRedirect } from "@/hooks/useInviteRedirect";

// ─── Schemas ──────────────────────────────────────────────────────────────────

const passwordSchema = z.object({
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

const magicLinkSchema = z.object({
  email: z.string().email("Enter a valid email address"),
});

type PasswordFormData = z.infer<typeof passwordSchema>;
type MagicLinkFormData = z.infer<typeof magicLinkSchema>;
const forgotPasswordSchema = z.object({
  email: z.string().email("Enter a valid email address"),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;
type Mode = "password" | "magic-link" | "org-selector" | "forgot-password";

// ─── Component ────────────────────────────────────────────────────────────────

export function LoginForm() {
  const t = useTranslations("auth");
  const { start: startProgress, done: stopProgress } = useNavigationProgress();
  useInviteRedirect();
  const [mode, setMode] = useState<Mode>("password");
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [showUnknownUser, setShowUnknownUser] = useState(false);

  // org-selector step state
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [isOrgPending, setIsOrgPending] = useState(false);
  const [orgError, setOrgError] = useState<string | null>(null);

  const passwordForm = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });

  const magicLinkForm = useForm<MagicLinkFormData>({
    resolver: zodResolver(magicLinkSchema),
    defaultValues: { email: "" },
  });

  const forgotPasswordForm = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });

  const isPasswordPending = passwordForm.formState.isSubmitting;
  const isMagicPending = magicLinkForm.formState.isSubmitting;
  const isForgotPending = forgotPasswordForm.formState.isSubmitting;

  async function onPasswordSubmit(data: PasswordFormData) {
    setShowUnknownUser(false);
    const result = await signIn(data.email, data.password);
    // If we reach here, signIn returned — either an error or org-selection needed.
    // A successful single-org login redirects (throws) and never returns.
    if (!result) return;
    if ("requiresOrgSelection" in result && result.requiresOrgSelection) {
      setOrgs(result.orgs);
      setSelectedOrgId(result.orgs[0]?.id ?? "");
      setMode("org-selector");
      return;
    }
    if (!result.success) {
      if (result.error === "UNKNOWN_USER") {
        setShowUnknownUser(true);
      } else {
        passwordForm.setError("root", { message: result.error });
      }
    }
  }

  async function onOrgSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsOrgPending(true);
    setOrgError(null);
    startProgress();
    try {
      const result = await selectOrg(selectedOrgId);
      // If we reach here, selectOrg returned an error (success redirects)
      if (result && !result.success) {
        setOrgError(result.error);
        stopProgress();
      }
    } catch {
      setOrgError("An unexpected error occurred");
      stopProgress();
    } finally {
      setIsOrgPending(false);
    }
  }

  async function onMagicLinkSubmit(data: MagicLinkFormData) {
    setShowUnknownUser(false);
    const result = await sendMagicLink(data.email);
    if (result.success) {
      setMagicLinkSent(true);
    } else {
      if (result.error === "UNKNOWN_USER") {
        setShowUnknownUser(true);
      } else {
        magicLinkForm.setError("root", { message: result.error });
      }
    }
  }

  async function onForgotPasswordSubmit(data: ForgotPasswordFormData) {
    const result = await requestPasswordReset(data.email);
    if (result.success) {
      setResetEmailSent(true);
    } else {
      forgotPasswordForm.setError("root", { message: result.error });
    }
  }

  function switchMode(next: "password" | "magic-link") {
    // Carry the email the user already typed across to the other form
    if (next === "magic-link") {
      const currentEmail = passwordForm.getValues("email");
      if (currentEmail) magicLinkForm.setValue("email", currentEmail);
    } else {
      const currentEmail = magicLinkForm.getValues("email");
      if (currentEmail) passwordForm.setValue("email", currentEmail);
    }
    setMode(next);
    setMagicLinkSent(false);
    setResetEmailSent(false);
    setShowUnknownUser(false);
    setOrgError(null);
    passwordForm.clearErrors();
    magicLinkForm.clearErrors();
    forgotPasswordForm.clearErrors();
  }

  // ── Org selector step ─────────────────────────────────────────────────────
  if (mode === "org-selector") {
    return (
      <div
        data-testid="org-selector"
        className="border-border bg-card rounded-xl border p-8"
      >
        <div className="bg-primary/10 mx-auto mb-4 flex size-12 items-center justify-center rounded-full">
          <Building2 className="text-primary size-6" />
        </div>
        <h2 className="text-center text-base font-semibold">
          {t("loginForm.orgSelectorTitle")}
        </h2>
        <p className="text-muted-foreground mt-1 text-center text-sm">
          {t("loginForm.orgSelectorBody")}
        </p>

        <form
          data-testid="org-selector-form"
          onSubmit={onOrgSubmit}
          className="mt-6 space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="org-select">
              {t("loginForm.orgSelectorLabel")}
            </Label>
            <Select value={selectedOrgId} onValueChange={setSelectedOrgId}>
              <SelectTrigger
                id="org-select"
                data-testid="org-select-trigger"
                className="w-full"
              >
                <SelectValue
                  placeholder={t("loginForm.orgSelectorPlaceholder")}
                />
              </SelectTrigger>
              <SelectContent>
                {orgs.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {orgError && (
            <p
              data-testid="org-selector-error"
              className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
            >
              {orgError}
            </p>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isOrgPending || !selectedOrgId}
            data-testid="org-confirm-button"
          >
            {isOrgPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <>
                {t("loginForm.continue")}
                <ArrowRight className="size-4" />
              </>
            )}
          </Button>
        </form>
      </div>
    );
  }

  // ── Forgot-password mode ────────────────────────────────────────────────
  if (mode === "forgot-password") {
    if (resetEmailSent) {
      return (
        <div
          data-testid="reset-email-sent"
          className="border-border bg-card rounded-xl border p-8 text-center"
        >
          <div className="bg-status-success mx-auto mb-4 flex size-12 items-center justify-center rounded-full">
            <CheckCircle2 className="text-status-success-fg size-6" />
          </div>
          <h2 className="text-base font-semibold">
            {t("loginForm.resetEmailSentTitle")}
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            {t("loginForm.resetEmailSentBody", {
              email: forgotPasswordForm.getValues("email"),
            })}
          </p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-4"
            onClick={() => switchMode("password")}
          >
            {t("loginForm.backToSignIn")}
          </Button>
        </div>
      );
    }

    return (
      <div className="border-border bg-card rounded-xl border p-6">
        <form
          data-testid="forgot-password-form"
          onSubmit={forgotPasswordForm.handleSubmit(onForgotPasswordSubmit)}
          className="space-y-4"
        >
          <div className="space-y-1">
            <h2 className="text-base font-semibold">
              {t("loginForm.forgotPasswordTitle")}
            </h2>
            <p className="text-muted-foreground text-sm">
              {t("loginForm.forgotPasswordHint")}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="forgot-email">{t("loginForm.emailLabel")}</Label>
            <Input
              id="forgot-email"
              type="email"
              autoComplete="email"
              placeholder={t("loginForm.emailPlaceholder")}
              aria-invalid={!!forgotPasswordForm.formState.errors.email}
              {...forgotPasswordForm.register("email")}
            />
            {forgotPasswordForm.formState.errors.email && (
              <p className="text-destructive text-xs">
                {forgotPasswordForm.formState.errors.email.message}
              </p>
            )}
          </div>

          {forgotPasswordForm.formState.errors.root && (
            <p
              data-testid="forgot-password-error"
              className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
            >
              {forgotPasswordForm.formState.errors.root.message}
            </p>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={isForgotPending}
            data-testid="send-reset-button"
          >
            {isForgotPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <>
                <Mail className="size-4" />
                {t("loginForm.sendResetLink")}
              </>
            )}
          </Button>

          <div className="text-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => switchMode("password")}
            >
              {t("loginForm.backToSignIn")}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  // ── Magic-link sent confirmation ──────────────────────────────────────────
  if (magicLinkSent) {
    return (
      <div
        data-testid="magic-link-sent"
        className="border-border bg-card rounded-xl border p-8 text-center"
      >
        <div className="bg-status-success mx-auto mb-4 flex size-12 items-center justify-center rounded-full">
          <CheckCircle2 className="text-status-success-fg size-6" />
        </div>
        <h2 className="text-base font-semibold">
          {t("loginForm.magicLinkSentTitle")}
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          {t("loginForm.magicLinkSentBody", {
            email: magicLinkForm.getValues("email"),
          })}
        </p>
        <Button
          variant="ghost"
          size="sm"
          className="mt-4"
          onClick={() => switchMode("password")}
        >
          {t("loginForm.backToSignIn")}
        </Button>
      </div>
    );
  }

  return (
    <div className="border-border bg-card rounded-xl border">
      {/* Mode tabs */}
      <div className="border-border flex border-b">
        <button
          type="button"
          onClick={() => switchMode("password")}
          className={[
            "focus-visible:ring-ring flex-1 py-3 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
            mode === "password"
              ? "text-foreground border-primary border-b-2"
              : "text-muted-foreground hover:text-foreground",
          ].join(" ")}
          aria-selected={mode === "password"}
          role="tab"
        >
          {t("loginForm.passwordTab")}
        </button>
        <button
          type="button"
          onClick={() => switchMode("magic-link")}
          className={[
            "focus-visible:ring-ring flex-1 py-3 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
            mode === "magic-link"
              ? "text-foreground border-primary border-b-2"
              : "text-muted-foreground hover:text-foreground",
          ].join(" ")}
          aria-selected={mode === "magic-link"}
          role="tab"
        >
          {t("loginForm.magicLinkTab")}
        </button>
      </div>

      <div className="p-6">
        {mode === "password" ? (
          // ── Email + password form ──────────────────────────────────────────
          <form
            data-testid="password-form"
            onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("loginForm.emailLabel")}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder={t("loginForm.emailPlaceholder")}
                aria-invalid={!!passwordForm.formState.errors.email}
                {...passwordForm.register("email")}
              />
              {passwordForm.formState.errors.email && (
                <p className="text-destructive text-xs">
                  {passwordForm.formState.errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password">{t("loginForm.passwordLabel")}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                aria-invalid={!!passwordForm.formState.errors.password}
                {...passwordForm.register("password")}
              />
              {passwordForm.formState.errors.password && (
                <p className="text-destructive text-xs">
                  {passwordForm.formState.errors.password.message}
                </p>
              )}
              <div className="text-right">
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  className="text-primary focus-visible:ring-ring text-xs font-medium hover:underline focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                  onClick={() => {
                    const currentEmail = passwordForm.getValues("email");
                    if (currentEmail)
                      forgotPasswordForm.setValue("email", currentEmail);
                    setMode("forgot-password");
                    setResetEmailSent(false);
                    forgotPasswordForm.clearErrors();
                  }}
                >
                  {t("loginForm.forgotPassword")}
                </button>
              </div>
            </div>

            {showUnknownUser && (
              <div
                data-testid="unknown-user-error"
                className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
              >
                <p>{t("loginForm.unknownUser")}</p>
                <p className="mt-1">
                  {t("loginForm.noAccount")}{" "}
                  <Link
                    href="/signup"
                    className="text-primary font-medium hover:underline"
                  >
                    {t("loginForm.signUpLink")}
                  </Link>
                </p>
              </div>
            )}

            {passwordForm.formState.errors.root && (
              <p
                data-testid="login-error"
                className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
              >
                {passwordForm.formState.errors.root.message}
              </p>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isPasswordPending}
              data-testid="sign-in-button"
            >
              {isPasswordPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <>
                  {t("loginForm.signIn")}
                  <ArrowRight className="size-4" />
                </>
              )}
            </Button>
          </form>
        ) : (
          // ── Magic link form ────────────────────────────────────────────────
          <form
            data-testid="magic-link-form"
            onSubmit={magicLinkForm.handleSubmit(onMagicLinkSubmit)}
            className="space-y-4"
          >
            <p className="text-muted-foreground text-sm">
              {t("loginForm.magicLinkHint")}
            </p>

            <div className="space-y-1.5">
              <Label htmlFor="magic-email">{t("loginForm.emailLabel")}</Label>
              <Input
                id="magic-email"
                type="email"
                autoComplete="email"
                placeholder={t("loginForm.emailPlaceholder")}
                aria-invalid={!!magicLinkForm.formState.errors.email}
                {...magicLinkForm.register("email")}
              />
              {magicLinkForm.formState.errors.email && (
                <p className="text-destructive text-xs">
                  {magicLinkForm.formState.errors.email.message}
                </p>
              )}
            </div>

            {showUnknownUser && (
              <div
                data-testid="unknown-user-error"
                className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
              >
                <p>{t("loginForm.unknownUser")}</p>
                <p className="mt-1">
                  {t("loginForm.noAccount")}{" "}
                  <Link
                    href="/signup"
                    className="text-primary font-medium hover:underline"
                  >
                    {t("loginForm.signUpLink")}
                  </Link>
                </p>
              </div>
            )}

            {magicLinkForm.formState.errors.root && (
              <p
                data-testid="magic-link-error"
                className="bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
              >
                {magicLinkForm.formState.errors.root.message}
              </p>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isMagicPending}
              data-testid="send-magic-link-button"
            >
              {isMagicPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <>
                  <Mail className="size-4" />
                  {t("loginForm.sendMagicLink")}
                </>
              )}
            </Button>
          </form>
        )}

        <p className="text-muted-foreground text-center text-sm">
          {t("loginForm.noAccount")}{" "}
          <Link
            href="/signup"
            className="text-primary font-medium hover:underline"
          >
            {t("loginForm.signUpLink")}
          </Link>
        </p>
      </div>
    </div>
  );
}
